<template>
  <div class="card zoom">
    <div v-if="topDate.platform === Platforms.TWITTER" class="header">
      <div class="row">
        <h3 class="title">{{ title }}</h3>
        <div class="tools">
          <RadioButtons
            :options="displayOptions"
            :active-option="activeDisplay"
            @select-option="changeDisplay($event)"
          />
          <button class="close-btn" @click="closesZoom">
            <i class="fa-solid fa-x"></i>
          </button>
        </div>
      </div>
      <div class="row sorters">
        <!-- Tweets Sort-->
        <DropdownMenu
          v-if="activeDisplay === VIEWS.PUBLICATIONS"
          v-model="selectedPublicationSort"
          :options="publicationSortOptions"
          label="Trier par :"
        />
        <!-- Articles Sort -->
        <DropdownMenu
          v-else
          v-model="selectedArticleSort"
          :options="articleSortOptions"
          label="Trier par :"
        />
      </div>
    </div>

    <div v-else class="header">
      <div class="row">
        <h3 class="title">{{ title }}</h3>

        <div class="row sorters">
          <DropdownMenu
            v-if="activeDisplay === VIEWS.PUBLICATIONS"
            v-model="selectedPublicationSort"
            :options="publicationSortOptions"
            label="Trier par :"
          />

          <button class="close-btn" @click="closesZoom">
            <i class="fa-solid fa-x"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="body">
      <div v-if="isPublicationsLoading" class="loading-wrapper">
        <MinerveOrbite />
      </div>

      <template
        v-else-if="
          activeDisplay === VIEWS.PUBLICATIONS && topPublications?.length > 0
        "
      >
        <PublicationCard
          v-for="post in topPublications"
          :key="post._id"
          :post="post"
          is-overview
        />
      </template>

      <template
        v-else-if="activeDisplay === VIEWS.ARTICLES && topArticles.length > 0"
      >
        <XArticleCard
          v-for="article in topArticles"
          :key="article._id"
          :article="article"
        />
      </template>
      <div v-else class="no-data-wrapper"><NoData /></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import MinerveOrbite from "@follaw-fr/components/reusable/minerve/MinerveOrbite.vue";
import { useDropdown } from "@follaw-fr/composables/useDropdown";

import XArticleCard from "@follaw-fr/components/social-media/molecules/XArticleCard.vue";
import PublicationCard from "@follaw-fr/components/social-media/posts/molecules/PublicationCard.vue";
import RadioButtons from "@follaw-fr/components/reusable/atoms/RadioButtons.vue";
import NoData from "@follaw-fr/components/reusable/atoms/NoData.vue";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";
import { Platforms } from "@follaw-fr/utils/social360-platforms.js";
import { getTimeCurveZoomTitle } from "@follaw-fr/utils/date.utils";
import { TIME_INTERVALS } from "@follaw-fr/constants/time-interval";

const props = defineProps({
  authorId: { type: String, required: false, default: null },
  isPublicationsLoading: { type: Boolean, required: true },
  topArticles: { type: Array, required: false, default: null },
  topArticlesCount: { type: Number, default: 0 },
  topArticlesFilters: { type: Object, required: false, default: null },
  topDate: { type: Object, required: true },
  topPublications: { type: Array, required: false, default: null },
  topPublicationsCount: { type: Number, default: 0 },
  interval: { type: String, required: false, default: TIME_INTERVALS.DAY },
});
const emit = defineEmits([
  "closesZoom",
  "updatePublicationsFilters",
  "updateTopArticlesFilters",
]);

const VIEWS = {
  PUBLICATIONS: "publications",
  ARTICLES: "links",
};

const displayOptions = [
  { label: "Publications", value: VIEWS.PUBLICATIONS },
  { label: "Articles", value: VIEWS.ARTICLES },
];

const activeDisplay = ref(VIEWS.PUBLICATIONS);

const defaultSortOptions = { like: "Like" };
const sortOptionsByPlatform = {
  [Platforms.TWITTER]: {
    retweet: "Repartage",
    impression: "Impressions",
    ...defaultSortOptions,
  },
  [Platforms.TIKTOK]: defaultSortOptions,
  [Platforms.LINKEDIN]: defaultSortOptions,
  [Platforms.INSTAGRAM]: defaultSortOptions,
  [Platforms.BLUESKY]: defaultSortOptions,
};

const publicationSelectedFilters = ref({
  sort_field: "like",
});
const {
  options: publicationSortOptions,
  selectedValue: selectedPublicationSort,
} = useDropdown({
  options: sortOptionsByPlatform[props.topDate.platform] || defaultSortOptions,
  selectedFilters: publicationSelectedFilters,
  filterKey: "sort_field",
  onChange: (updatedValue) => {
    emit("updatePublicationsFilters", {
      sort_field: updatedValue,
      sort_order: "desc",
    });
  },
});

const articlesSortOptions = {
  number_of_tweets: "Publications",
  number_of_retweets: "Repartage",
};
const articlesSelectedFilters = ref({
  ...props.topArticlesFilters,
});
const { options: articleSortOptions, selectedValue: selectedArticleSort } =
  useDropdown({
    options: articlesSortOptions,
    selectedFilters: articlesSelectedFilters,
    filterKey: "sort_field",
    onChange: (updatedValue) => {
      emit("updateTopArticlesFilters", {
        sort_field: updatedValue,
        sort_order: "desc",
      });
    },
  });

const title = computed(() =>
  getTimeCurveZoomTitle({
    count:
      activeDisplay.value === VIEWS.PUBLICATIONS
        ? props.topPublicationsCount
        : props.topArticlesCount,
    date: props.topDate.date,
    interval: props.interval,
  })
);

const changeDisplay = (event) => {
  activeDisplay.value = event;
};

const closesZoom = () => {
  emit("closesZoom");
};
</script>

<style lang="scss" scoped>
.card.zoom {
  padding: 16px 4px 4px 4px;
}

.close-btn {
  font-size: 0.8em;
  width: 25px;
  height: 25px;
  padding: 4px;
  border-radius: 50%;

  &:hover {
    background-color: var(--clr-p5);
    color: white;
  }
}

.header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  margin-bottom: 5px;
  padding: 0 11px;
  padding-bottom: 4px;

  .row {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.sorters {
      flex: 1;
      justify-content: flex-end;
      gap: 16px;
    }
  }

  .title {
    margin: 0;
    font-size: 1em;
  }

  .tools {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
  height: 400px;
  padding: 0;
  overflow-y: auto;

  .loading-wrapper,
  .no-data-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    width: 50%;
    height: 100%;
  }
}

/* Scrollbar
========================================================================== */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-thumb {
  border-radius: 7px;
  background-color: var(--clr-p4);
}
</style>
